import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { format, addDays, subDays } from "date-fns";
import { es } from "date-fns/locale";
import {
  Calendar, Users, DollarSign, Bell, Check, Send,
  ChevronLeft, ChevronRight, RefreshCw, MessageCircle, Filter,
  TrendingUp, Package, FileText, Download, Plus, Trash2,
  LogOut, BarChart3, AlertTriangle, Copy, Share2, Settings, Save, MapPin, Clock, Scissors, Globe
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FRONTEND_URL = "https://booseenterprise.com";

// 🌍 EL DICCIONARIO DEL SISTEMA (Motor de Traducción) 🌍
const TRANSLATIONS = {
  es: {
    dashboard: "Dashboard", appointments: "Citas", cuts: "Cortes", inventory: "Inventario", finances: "Finanzas", reports: "Informes", settings: "Ajustes",
    today_appointments: "Citas Hoy", today_cuts: "Cortes Hoy", today_income: "Ingresos Hoy", today_profit: "Ganancia Hoy",
    this_month: "Este Mes", income: "Ingresos", expenses: "Gastos", profit: "Ganancia", month_cuts: "Cortes del Mes", alerts: "Alertas",
    low_stock: "Stock bajo", pending_appointments: "Citas pendientes", save_changes: "Guardar Todos los Cambios"
  },
  en: {
    dashboard: "Dashboard", appointments: "Appointments", cuts: "Cuts", inventory: "Inventory", finances: "Finances", reports: "Reports", settings: "Settings",
    today_appointments: "Appointments Today", today_cuts: "Cuts Today", today_income: "Income Today", today_profit: "Profit Today",
    this_month: "This Month", income: "Income", expenses: "Expenses", profit: "Profit", month_cuts: "Month's Cuts", alerts: "Alerts",
    low_stock: "Low stock", pending_appointments: "Pending appts", save_changes: "Save All Changes"
  },
  pt: {
    dashboard: "Painel", appointments: "Agendamentos", cuts: "Cortes", inventory: "Estoque", finances: "Finanças", reports: "Relatórios", settings: "Configurações",
    today_appointments: "Agendamentos Hoje", today_cuts: "Cortes Hoje", today_income: "Renda Hoje", today_profit: "Lucro Hoje",
    this_month: "Este Mês", income: "Renda", expenses: "Despesas", profit: "Lucro", month_cuts: "Cortes do Mês", alerts: "Alertas",
    low_stock: "Estoque baixo", pending_appointments: "Agendamentos pendentes", save_changes: "Salvar Todas as Alterações"
  },
  fr: {
    dashboard: "Tableau de Bord", appointments: "Rendez-vous", cuts: "Coupes", inventory: "Inventaire", finances: "Finances", reports: "Rapports", settings: "Paramètres",
    today_appointments: "Rendez-vous Auj.", today_cuts: "Coupes Auj.", today_income: "Revenus Auj.", today_profit: "Bénéfice Auj.",
    this_month: "Ce Mois", income: "Revenus", expenses: "Dépenses", profit: "Bénéfice", month_cuts: "Coupes du Mois", alerts: "Alertes",
    low_stock: "Stock faible", pending_appointments: "Rendez-vous en attente", save_changes: "Enregistrer les modifications"
  }
};

const STATUS_LABELS = { pending: "Pendiente", confirmed: "Confirmada", completed: "Completada", cancelled: "Cancelada" };

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState("dashboard");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [cuts, setCuts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({ today: {}, month: {}, alerts: {} });
  const [loading, setLoading] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState({ configured: false });
  const [adminProfile, setAdminProfile] = useState(null);

  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), "yyyy-MM-dd"),
    end: format(new Date(), "yyyy-MM-dd")
  });

  const [notificationModal, setNotificationModal] = useState(false);
  const [transactionModal, setTransactionModal] = useState(false);
  const [inventoryModal, setInventoryModal] = useState(false);
  const [cutModal, setCutModal] = useState(false);

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [sendWhatsApp, setSendWhatsApp] = useState(true);

  // 🟢 ESTADO PARA CARGA DE NOTIFICACIÓN PUSH
  const [notifyingId, setNotifyingId] = useState(null);

  const [newTransaction, setNewTransaction] = useState({
    type: "expense", category: "", amount: "", description: "", date: format(new Date(), "yyyy-MM-dd")
  });
  const [newInventoryItem, setNewInventoryItem] = useState({
    name: "", category: "", quantity: "", min_quantity: "5", unit_price: "", supplier: ""
  });
  const [newCut, setNewCut] = useState({
    client_name: "", service: "", price: "", barber_name: "", notes: "",
    date: format(new Date(), "yyyy-MM-dd"), time: format(new Date(), "HH:mm")
  });
  const [settingsForm, setSettingsForm] = useState({
    phone: "", address: "", logo_url: "", business_hours: { start: 9, end: 21 },
    services: [], currency: "DOP", language: "es"
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const [aptsRes, statsRes, whatsappRes, cutsRes, invRes, transRes, profileRes] = await Promise.all([
        axios.get(`${API}/appointments?date=${dateStr}`, { headers }),
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/whatsapp/status`),
        axios.get(`${API}/cuts?start_date=${dateRange.start}&end_date=${dateRange.end}`, { headers }),
        axios.get(`${API}/inventory`, { headers }),
        axios.get(`${API}/transactions?start_date=${dateRange.start}&end_date=${dateRange.end}`, { headers }),
        axios.get(`${API}/admin/me`, { headers })
      ]);
      setAppointments(aptsRes.data);
      setStats(statsRes.data);
      setWhatsappStatus(whatsappRes.data);
      setCuts(cutsRes.data);
      setInventory(invRes.data);
      setTransactions(transRes.data);
      const profile = profileRes.data;
      setAdminProfile(profile);
      setSettingsForm({
        phone: profile.phone || "", address: profile.address || "", logo_url: profile.logo_url || "",
        business_hours: profile.business_hours || { start: 9, end: 21 }, services: profile.services || [],
        currency: profile.currency || "DOP", language: profile.language || "es"
      });
    } catch (error) {
      if (error.response?.status === 401) { logout(); navigate("/admin/login"); }
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, dateRange, token, navigate, logout]); // eslint-disable-line

  useEffect(() => { fetchData(); }, [fetchData]);

  const bookingLink = adminProfile?.slug ? `${FRONTEND_URL}/${adminProfile.slug}` : null;
  const currentCurrency = adminProfile?.currency || 'DOP';
  const currentLanguage = adminProfile?.language || 'es';
  const t = (key) => TRANSLATIONS[currentLanguage]?.[key] || TRANSLATIONS['es'][key] || key;

  const copyBookingLink = () => {
    if (bookingLink) { navigator.clipboard.writeText(bookingLink); toast.success("¡Link copiado al portapapeles!"); }
  };

  // 🔔 LLAMAR AL CLIENTE (Gatillo Push VIP)
  const handleNotifyTurn = async (appointmentId) => {
    setNotifyingId(appointmentId);
    try {
      const response = await axios.post(`${API}/appointments/${appointmentId}/notify`, {}, { headers });
      if (response.data.success) {
        toast.success("Aviso enviado 🔔", {
          description: "El cliente ha recibido la alerta en su móvil.",
          style: { background: '#101014', color: '#f59e0b', border: '1px solid #f59e0b' }
        });
      }
    } catch (error) {
      console.error("Error notifying client:", error);
      toast.error("El cliente no tiene instalada la App o no tiene Token.");
    } finally {
      setNotifyingId(null);
    }
  };

  const handleAddService = () => {
    setSettingsForm(prev => ({ ...prev, services: [...prev.services, { id: `new-${Date.now()}`, name: "", price: 0, duration: 30 }] }));
  };

  const handleServiceChange = (index, field, value) => {
    const newServices = [...settingsForm.services];
    newServices[index][field] = value;
    setSettingsForm(prev => ({ ...prev, services: newServices }));
  };

  const handleRemoveService = (index) => {
    setSettingsForm(prev => ({ ...prev, services: prev.services.filter((_, i) => i !== index) }));
  };

  const handleBusinessHoursChange = (field, value) => {
    setSettingsForm(prev => ({ ...prev, business_hours: { ...prev.business_hours, [field]: parseInt(value) || 0 } }));
  };

  const handleSettingsFieldChange = (field, value) => {
    setSettingsForm(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdateSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, settingsForm, { headers });
      toast.success("Configuración guardada correctamente");
      fetchData();
    } catch (error) { toast.error("Error al guardar la configuración"); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axios.put(`${API}/appointments/${id}`, { status }, { headers });
      toast.success(`Estado: ${STATUS_LABELS[status]}`);
      fetchData();
    } catch (error) { toast.error("Error al actualizar"); }
  };

  const handleSendNotification = async () => {
    if (!notificationMessage.trim()) return toast.error("Escribe un mensaje");
    try {
      const res = await axios.post(`${API}/notifications`, { appointment_id: selectedAppointment.id, message: notificationMessage, send_whatsapp: sendWhatsApp }, { headers });
      toast.success(res.data.whatsapp_sent ? "WhatsApp enviado" : "Notificación guardada");
      setNotificationModal(false);
      fetchData();
    } catch (error) { toast.error("Error al enviar"); }
  };

  const handleCreateTransaction = async () => {
    if (!newTransaction.category || !newTransaction.amount) return toast.error("Completa los campos");
    try {
      await axios.post(`${API}/transactions`, { ...newTransaction, amount: parseFloat(newTransaction.amount) }, { headers });
      toast.success("Transacción creada");
      setTransactionModal(false);
      setNewTransaction({ type: "expense", category: "", amount: "", description: "", date: format(new Date(), "yyyy-MM-dd") });
      fetchData();
    } catch (error) { toast.error("Error al crear transacción"); }
  };

  const handleDeleteTransaction = async (id) => {
    try { await axios.delete(`${API}/transactions/${id}`, { headers }); toast.success("Transacción eliminada"); fetchData(); }
    catch (error) { toast.error("Error al eliminar"); }
  };

  // 📦 Agregar al inventario Y registrar Gasto automáticamente
  const handleCreateInventoryItem = async () => {
    if (!newInventoryItem.name || !newInventoryItem.quantity) return toast.error("Completa los campos");
    try {
      const cantidad = parseInt(newInventoryItem.quantity);
      const precioUnitario = parseFloat(newInventoryItem.unit_price || 0);
      const costoTotal = cantidad * precioUnitario;

      await axios.post(`${API}/inventory`, {
        ...newInventoryItem,
        quantity: cantidad,
        min_quantity: parseInt(newInventoryItem.min_quantity),
        unit_price: precioUnitario
      }, { headers });

      if (costoTotal > 0) {
        await axios.post(`${API}/transactions`, {
          type: "expense", category: "Inventario", amount: costoTotal,
          description: `Compra de ${cantidad}x ${newInventoryItem.name}`,
          date: format(new Date(), "yyyy-MM-dd")
        }, { headers });
        toast.success("Item agregado y Gasto registrado en Finanzas");
      } else {
        toast.success("Item agregado al inventario");
      }

      setInventoryModal(false);
      setNewInventoryItem({ name: "", category: "", quantity: "", min_quantity: "5", unit_price: "", supplier: "" });
      fetchData();
    } catch (error) { toast.error("Error al crear item"); }
  };

  const handleUpdateInventory = async (id, quantity) => {
    try { await axios.put(`${API}/inventory/${id}`, { quantity: parseInt(quantity) }, { headers }); toast.success("Inventario actualizado"); fetchData(); }
    catch (error) { toast.error("Error al actualizar"); }
  };

  const handleDeleteInventoryItem = async (id) => {
    try { await axios.delete(`${API}/inventory/${id}`, { headers }); toast.success("Item eliminado"); fetchData(); }
    catch (error) { toast.error("Error al eliminar"); }
  };

  const handleCreateCut = async () => {
    if (!newCut.client_name || !newCut.service) return toast.error("Cliente y servicio son requeridos");
    try {
      await axios.post(`${API}/cuts`, { ...newCut, price: parseInt(newCut.price) }, { headers });
      toast.success("Corte registrado (El ingreso se sumó a Finanzas automáticamente)");
      setCutModal(false);
      setNewCut({ client_name: "", service: "", price: "", barber_name: "", notes: "", date: format(new Date(), "yyyy-MM-dd"), time: format(new Date(), "HH:mm") });
      fetchData();
    } catch (error) { toast.error("Error al registrar corte"); }
  };

  const handleExport = async (type) => {
    try {
      const response = await axios.get(`${API}/reports/export/${type}?start_date=${dateRange.start}&end_date=${dateRange.end}`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_${dateRange.start}_${dateRange.end}.${type === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(`${type.toUpperCase()} descargado`);
    } catch (error) { toast.error("Error al exportar"); }
  };

  const handleLogout = () => { logout(); navigate("/"); };

  const getStatusClass = (status) => ({
    pending: "text-amber-500", confirmed: "text-blue-500",
    completed: "text-green-500", cancelled: "text-red-500"
  }[status] || "text-amber-500");

  const filteredAppointments = statusFilter === "all" ? appointments : appointments.filter(a => a.status === statusFilter);
  const lowStockItems = inventory.filter(i => i.quantity < i.min_quantity);
  const totalIncome = transactions.filter(t => t.type === "income").reduce((sum, t) => sum + t.amount, 0);
  const totalExpenses = transactions.filter(t => t.type === "expense").reduce((sum, t) => sum + t.amount, 0);

  const getServiceName = (serviceIdOrName) => {
    if (!adminProfile?.services) return serviceIdOrName;
    const found = adminProfile.services.find(s => s.id === serviceIdOrName || s.name === serviceIdOrName);
    return found ? found.name : serviceIdOrName;
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* ====== HEADER ====== */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 pt-14 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={adminProfile?.logo_url || "/logo192.png"} alt="Logo"
              className="w-10 h-10 object-cover rounded-lg bg-zinc-900 border border-amber-500/50"
              onError={(e) => { e.target.onerror = null; e.target.src = "/logo192.png"; }}
            />
            <span className="font-heading text-xl font-bold uppercase tracking-wider text-white truncate max-w-[150px] sm:max-w-none">
              {user?.shop_name || "Barbershop"}
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {whatsappStatus.configured && (
              <span className="hidden md:flex items-center text-green-500 text-xs bg-green-500/10 px-2 py-1 border border-green-500/20">
                <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
              </span>
            )}
            <Button onClick={fetchData} variant="ghost" size="icon" className="text-zinc-400 hover:text-white">
              <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button onClick={handleLogout} variant="ghost" size="icon" className="text-zinc-400 hover:text-white">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* ====== NAV TABS ====== */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: "dashboard", label: t("dashboard"), icon: BarChart3 },
            { id: "appointments", label: t("appointments"), icon: Calendar },
            { id: "cuts", label: t("cuts"), icon: Users },
            { id: "inventory", label: t("inventory"), icon: Package },
            { id: "finances", label: t("finances"), icon: DollarSign },
            { id: "reports", label: t("reports"), icon: FileText },
            { id: "settings", label: t("settings"), icon: Settings }
          ].map(({ id, label, icon: Icon }) => (
            <Button key={id} onClick={() => setActiveSection(id)}
              className={`font-heading uppercase text-xs ${activeSection === id ? "bg-amber-500 text-black" : "bg-zinc-800 text-zinc-400 hover:text-white"}`}>
              <Icon className="w-4 h-4 mr-2" /> {label}
            </Button>
          ))}
        </div>

        {/* ============================== */}
        {/* DASHBOARD */}
        {/* ============================== */}
        {activeSection === "dashboard" && (
          <div className="space-y-6">
            {bookingLink && (
              <div className="bg-gradient-to-r from-amber-500/20 to-amber-500/5 border border-amber-500/30 p-6 rounded-lg">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <h3 className="font-heading text-lg font-bold uppercase text-amber-500 flex items-center gap-2">
                    <Share2 className="w-5 h-5" /> Tu Link de Reservas
                  </h3>
                  <div className="flex flex-col sm:flex-row items-center gap-2">
                    <div className="bg-zinc-900 border border-zinc-700 px-4 py-3 text-white text-sm truncate w-full sm:max-w-[300px] rounded-md">{bookingLink}</div>
                    <Button onClick={copyBookingLink} className="w-full sm:w-auto h-11 bg-amber-500 text-black hover:bg-amber-400 font-heading uppercase text-xs">
                      <Copy className="w-4 h-4 mr-2" /> Copiar
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-lg">
                <div className="flex items-center gap-2 mb-2"><Calendar className="w-4 h-4 text-amber-500" /><span className="text-zinc-500 text-xs uppercase">{t("today_appointments")}</span></div>
                <p className="font-heading text-3xl font-bold text-white">{stats.today?.appointments || 0}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-lg">
                <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-amber-500" /><span className="text-zinc-500 text-xs uppercase">{t("today_cuts")}</span></div>
                <p className="font-heading text-3xl font-bold text-white">{stats.today?.cuts || 0}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-lg">
                <div className="flex items-center gap-2 mb-2"><DollarSign className="w-4 h-4 text-green-500" /><span className="text-zinc-500 text-xs uppercase">{t("today_income")}</span></div>
                <p className="font-heading text-2xl font-bold text-green-500">{stats.today?.income || 0} {currentCurrency}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-lg">
                <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-amber-500" /><span className="text-zinc-500 text-xs uppercase">{t("today_profit")}</span></div>
                <p className="font-heading text-2xl font-bold text-amber-500">{stats.today?.profit || 0} {currentCurrency}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-lg">
                <h3 className="font-heading text-lg font-bold uppercase text-white mb-4">{t("this_month")}</h3>
                <div className="space-y-3">
                  <div className="flex justify-between"><span className="text-zinc-400">{t("income")}:</span><span className="text-green-500 font-bold">{stats.month?.income || 0} {currentCurrency}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-400">{t("expenses")}:</span><span className="text-red-500 font-bold">{stats.month?.expenses || 0} {currentCurrency}</span></div>
                  <div className="flex justify-between border-t border-zinc-800 pt-3"><span className="text-white font-bold">{t("profit")}:</span><span className="text-amber-500 font-bold">{stats.month?.profit || 0} {currentCurrency}</span></div>
                </div>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-lg">
                <h3 className="font-heading text-lg font-bold uppercase text-white mb-4">{t("month_cuts")}</h3>
                <p className="font-heading text-5xl font-bold text-amber-500">{stats.month?.cuts || 0}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-lg">
                <h3 className="font-heading text-lg font-bold uppercase text-white mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />{t("alerts")}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-zinc-400">{t("low_stock")}:</span><span className={`font-bold ${stats.alerts?.low_stock > 0 ? 'text-red-500' : 'text-green-500'}`}>{stats.alerts?.low_stock || 0}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-400">{t("pending_appointments")}:</span><span className="text-amber-500 font-bold">{stats.alerts?.pending_appointments || 0}</span></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================== */}
        {/* CITAS — con botón "Llamar Cliente" */}
        {/* ============================== */}
        {activeSection === "appointments" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
            <div className="lg:col-span-4">
              <div className="bg-zinc-900 border border-zinc-800 p-4 sticky top-24 rounded-lg shadow-xl">
                <CalendarComponent mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} locale={es} className="text-white mx-auto" />
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-2 bg-zinc-900 p-1 border border-zinc-800 rounded-lg">
                  <Button onClick={() => setSelectedDate(subDays(selectedDate, 1))} variant="ghost" className="text-zinc-400 p-2 hover:bg-zinc-800"><ChevronLeft className="w-5 h-5" /></Button>
                  <h2 className="font-heading text-base font-bold uppercase text-white px-2">{format(selectedDate, "EEEE d MMM", { locale: es })}</h2>
                  <Button onClick={() => setSelectedDate(addDays(selectedDate, 1))} variant="ghost" className="text-zinc-400 p-2 hover:bg-zinc-800"><ChevronRight className="w-5 h-5" /></Button>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[150px] bg-zinc-900 border-zinc-800 text-white text-sm">
                    <Filter className="w-3 h-3 mr-2" /><SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="pending">Pendientes</SelectItem>
                    <SelectItem value="confirmed">Confirmadas</SelectItem>
                    <SelectItem value="completed">Completadas</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filteredAppointments.length === 0 ? (
                <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-lg">
                  <Calendar className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500">Sin citas para este día</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredAppointments.map((apt) => (
                    <div key={apt.id} className="bg-zinc-900 border border-zinc-800 p-4 rounded-lg hover:border-zinc-700 transition-colors">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-heading text-xl font-bold text-amber-500">{apt.time}</span>
                            <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-1 border border-current ${getStatusClass(apt.status)}`}>
                              {STATUS_LABELS[apt.status]}
                            </span>
                            {apt.queue_position !== undefined && (apt.status === "pending" || apt.status === "confirmed") && (
                              <span className="text-xs bg-zinc-800 px-2 py-1 text-zinc-400 rounded border border-zinc-700">Cola: {apt.queue_position}</span>
                            )}
                          </div>
                          <p className="text-white font-semibold text-lg">{apt.client_name}</p>
                          <p className="text-zinc-500 text-sm mt-1">
                            <span className="text-amber-500">{apt.client_phone}</span> • {getServiceName(apt.service)}
                          </p>
                        </div>
                        <div className="flex items-center justify-between md:justify-end gap-2 w-full md:w-auto mt-2 md:mt-0 pt-3 border-t border-zinc-800 md:border-0 md:pt-0 flex-wrap">
                          <span className="font-heading text-lg font-bold text-green-500">{apt.price} {currentCurrency}</span>
                          <div className="flex gap-2 flex-wrap">

                            {/* 🚀 BOTÓN ESTRELLA: LLAMAR CLIENTE (Push Notification) */}
                            {(apt.status === "pending" || apt.status === "confirmed") && (
                              <Button
                                onClick={() => handleNotifyTurn(apt.id)}
                                disabled={notifyingId === apt.id}
                                className="bg-amber-500/10 hover:bg-amber-500 text-amber-500 hover:text-black border border-amber-500/30 font-heading uppercase text-[10px] tracking-widest px-3 h-9 rounded-lg transition-all"
                              >
                                {notifyingId === apt.id
                                  ? <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                                  : <Bell className="w-4 h-4 mr-1 animate-pulse" />
                                }
                                Llamar
                              </Button>
                            )}

                            {/* Botón WhatsApp */}
                            <Button
                              onClick={() => { setSelectedAppointment(apt); setNotificationMessage(`Hola ${apt.client_name}, tu turno se acerca.`); setNotificationModal(true); }}
                              size="icon" variant="outline" className="border-green-500/50 text-green-500 hover:bg-green-500/10 h-9 w-9" title="Enviar WhatsApp"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>

                            {/* Botón Confirmar */}
                            {apt.status === "pending" && (
                              <Button onClick={() => handleStatusChange(apt.id, "confirmed")} size="icon" className="bg-blue-500/10 hover:bg-blue-500 text-blue-500 hover:text-white h-9 w-9" title="Confirmar">
                                <Check className="w-4 h-4" />
                              </Button>
                            )}

                            {/* Botón Completar */}
                            {(apt.status === "pending" || apt.status === "confirmed") && (
                              <Button onClick={() => handleStatusChange(apt.id, "completed")} size="icon" className="bg-green-500/10 hover:bg-green-500 text-green-500 hover:text-black h-9 w-9" title="Completar">
                                <Check className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================== */}
        {/* CORTES */}
        {/* ============================== */}
        {activeSection === "cuts" && (
          <div className="space-y-4 mt-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold uppercase text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-500" /> Cortes Realizados
              </h2>
              <Button onClick={() => setCutModal(true)} className="bg-amber-500 text-black hover:bg-amber-400 font-heading uppercase text-xs">
                <Plus className="w-4 h-4 mr-2" /> Registrar Corte
              </Button>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 overflow-hidden rounded-xl shadow-xl">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-950">
                    <tr>
                      {["Fecha", "Cliente", "Servicio", "Precio", "Barbero"].map(h => (
                        <th key={h} className="text-left p-4 text-zinc-500 font-heading uppercase text-xs">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {cuts.slice(0, 50).map((cut) => (
                      <tr key={cut.id} className="hover:bg-zinc-800/50 transition-colors">
                        <td className="p-4 text-white whitespace-nowrap">{cut.date} <span className="text-zinc-500 ml-1">{cut.time}</span></td>
                        <td className="p-4 text-white font-bold">{cut.client_name}</td>
                        <td className="p-4 text-zinc-400">{getServiceName(cut.service)}</td>
                        <td className="p-4 text-green-500 font-bold">{cut.price} {currentCurrency}</td>
                        <td className="p-4 text-zinc-400">{cut.barber_name || "-"}</td>
                      </tr>
                    ))}
                    {cuts.length === 0 && (
                      <tr><td colSpan="5" className="p-12 text-center text-zinc-500">No hay cortes registrados en este período</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ============================== */}
        {/* INVENTARIO */}
        {/* ============================== */}
        {activeSection === "inventory" && (
          <div className="space-y-4 mt-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold uppercase text-white flex items-center gap-2">
                <Package className="w-5 h-5 text-amber-500" /> Inventario
              </h2>
              <Button onClick={() => setInventoryModal(true)} className="bg-amber-500 text-black hover:bg-amber-400 font-heading uppercase text-xs">
                <Plus className="w-4 h-4 mr-2" /> Agregar Item
              </Button>
            </div>
            {lowStockItems.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-lg flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
                <div>
                  <span className="font-heading uppercase font-bold text-red-500 block mb-1">Alerta de Stock Bajo</span>
                  <p className="text-red-400/80 text-sm">Productos por agotarse: <span className="text-red-300 font-semibold">{lowStockItems.map(i => i.name).join(", ")}</span></p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {inventory.map((item) => (
                <div key={item.id} className={`bg-zinc-900 border rounded-xl p-5 shadow-lg transition-all ${item.quantity < item.min_quantity ? 'border-red-500/50' : 'border-zinc-800'}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-white font-semibold text-lg">{item.name}</h3>
                      <p className="text-zinc-500 text-xs mt-1 uppercase tracking-wider">{item.category}</p>
                    </div>
                    <Button onClick={() => handleDeleteInventoryItem(item.id)} variant="ghost" size="icon" className="text-zinc-500 hover:text-red-500 hover:bg-red-500/10 h-8 w-8">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-zinc-800">
                    <div>
                      <p className="text-zinc-500 text-[10px] uppercase font-bold tracking-widest mb-1">Stock</p>
                      <div className="flex items-baseline gap-1">
                        <span className={`font-heading text-3xl font-black ${item.quantity < item.min_quantity ? 'text-red-500' : 'text-white'}`}>{item.quantity}</span>
                        <span className="text-zinc-500 text-xs">/ {item.min_quantity}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 bg-zinc-950 border border-zinc-800 rounded-lg p-1">
                      <Button variant="ghost" size="icon" onClick={() => handleUpdateInventory(item.id, item.quantity - 1)} className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-800">-</Button>
                      <Button variant="ghost" size="icon" onClick={() => handleUpdateInventory(item.id, item.quantity + 1)} className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-800">+</Button>
                    </div>
                  </div>
                </div>
              ))}
              {inventory.length === 0 && (
                <div className="col-span-full py-16 text-center bg-zinc-900 border border-zinc-800 rounded-xl">
                  <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-400 text-lg">Tu inventario está vacío</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================== */}
        {/* FINANZAS */}
        {/* ============================== */}
        {activeSection === "finances" && (
          <div className="space-y-6 mt-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h2 className="font-heading text-xl font-bold uppercase text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-amber-500" /> Finanzas
              </h2>
              <div className="flex flex-wrap items-center gap-2 bg-zinc-900 p-2 rounded-lg border border-zinc-800">
                <Input type="date" value={dateRange.start} onChange={(e) => setDateRange({...dateRange, start: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white w-full sm:w-[140px] h-9 text-xs" />
                <span className="text-zinc-500 hidden sm:inline">-</span>
                <Input type="date" value={dateRange.end} onChange={(e) => setDateRange({...dateRange, end: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white w-full sm:w-[140px] h-9 text-xs" />
                <Button onClick={() => setTransactionModal(true)} className="w-full sm:w-auto bg-amber-500 text-black hover:bg-amber-400 font-heading uppercase text-xs h-9">
                  <Plus className="w-4 h-4 mr-1" /> Nueva Transacción
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-xl relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-24 h-24 bg-green-500/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110" />
                <span className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-2">Total Ingresos</span>
                <p className="font-heading text-4xl font-black text-green-500">{totalIncome.toFixed(0)} <span className="text-sm text-green-500/50">{currentCurrency}</span></p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-xl relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-24 h-24 bg-red-500/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110" />
                <span className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-2">Total Gastos</span>
                <p className="font-heading text-4xl font-black text-red-500">{totalExpenses.toFixed(0)} <span className="text-sm text-red-500/50">{currentCurrency}</span></p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/10 rounded-bl-full -mr-4 -mt-4" />
                <span className="text-zinc-400 text-xs font-bold uppercase tracking-wider block mb-2">Ganancia Neta</span>
                <p className="font-heading text-4xl font-black text-amber-500">{(totalIncome - totalExpenses).toFixed(0)} <span className="text-sm text-amber-500/50">{currentCurrency}</span></p>
              </div>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 overflow-hidden rounded-xl shadow-xl">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-950">
                    <tr>
                      {["Fecha", "Tipo", "Detalles", "Monto", ""].map((h, i) => (
                        <th key={i} className={`${i === 3 ? "text-right" : "text-left"} p-5 text-zinc-500 font-heading uppercase text-xs tracking-wider`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {transactions.slice(0, 50).map((tr) => (
                      <tr key={tr.id} className="hover:bg-zinc-800/50 transition-colors">
                        <td className="p-5 text-white whitespace-nowrap">{tr.date}</td>
                        <td className="p-5">
                          <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded border ${tr.type === 'income' ? 'bg-green-500/10 text-green-500 border-green-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}`}>
                            {tr.type === 'income' ? 'Ingreso' : 'Gasto'}
                          </span>
                        </td>
                        <td className="p-5">
                          <p className="text-white font-medium">{tr.category}</p>
                          <p className="text-zinc-500 text-xs mt-1">{tr.description}</p>
                        </td>
                        <td className={`p-5 text-right font-bold whitespace-nowrap text-base ${tr.type === 'income' ? 'text-green-500' : 'text-red-500'}`}>
                          {tr.type === 'income' ? '+' : '-'}{tr.amount} {currentCurrency}
                        </td>
                        <td className="p-5 text-right">
                          <Button onClick={() => handleDeleteTransaction(tr.id)} variant="ghost" size="icon" className="text-zinc-500 hover:text-red-500 hover:bg-red-500/10 h-8 w-8 rounded-full">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {transactions.length === 0 && (
                      <tr><td colSpan="5" className="p-12 text-center text-zinc-500">No hay transacciones registradas.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ============================== */}
        {/* REPORTES */}
        {/* ============================== */}
        {activeSection === "reports" && (
          <div className="space-y-6 mt-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h2 className="font-heading text-xl font-bold uppercase text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-500" /> Informes y Reportes
              </h2>
              <div className="flex items-center gap-2 bg-zinc-900 p-2 rounded-lg border border-zinc-800">
                <Input type="date" value={dateRange.start} onChange={(e) => setDateRange({...dateRange, start: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white w-full sm:w-[140px] h-9 text-xs" />
                <span className="text-zinc-500">-</span>
                <Input type="date" value={dateRange.end} onChange={(e) => setDateRange({...dateRange, end: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white w-full sm:w-[140px] h-9 text-xs" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-xl shadow-lg">
                <h3 className="text-zinc-400 text-xs font-bold uppercase tracking-wider mb-6 pb-4 border-b border-zinc-800">Resumen del Período</h3>
                <div className="space-y-5">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-medium">Total Cortes:</span>
                    <span className="bg-zinc-800 text-white px-3 py-1 rounded-md font-bold">{cuts.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400">Total Ingresos:</span>
                    <span className="font-heading text-xl font-bold text-green-500">{totalIncome.toFixed(0)} {currentCurrency}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400">Total Gastos:</span>
                    <span className="font-heading text-xl font-bold text-red-500">{totalExpenses.toFixed(0)} {currentCurrency}</span>
                  </div>
                  <div className="flex justify-between items-center border-t border-zinc-800 pt-5">
                    <span className="text-amber-500 font-bold uppercase tracking-wider">Ganancia Neta:</span>
                    <span className="font-heading text-3xl font-black text-amber-500">{(totalIncome - totalExpenses).toFixed(0)} <span className="text-sm">{currentCurrency}</span></span>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-xl shadow-lg flex flex-col">
                <h3 className="text-zinc-400 text-xs font-bold uppercase tracking-wider mb-2">Exportar Datos</h3>
                <p className="text-zinc-500 text-sm mb-8">Descarga un documento detallado con cortes, clientes, ingresos y gastos del período seleccionado.</p>
                <div className="mt-auto space-y-3">
                  <Button onClick={() => handleExport('excel')} className="w-full bg-[#107c41] text-white hover:bg-[#0c5c30] font-heading uppercase h-14 text-sm shadow-lg">
                    <Download className="w-5 h-5 mr-3" /> Exportar a Excel (.xlsx)
                  </Button>
                  <Button onClick={() => handleExport('pdf')} className="w-full bg-red-600 text-white hover:bg-red-700 font-heading uppercase h-14 text-sm shadow-lg">
                    <FileText className="w-5 h-5 mr-3" /> Generar PDF Listo para Imprimir
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-xl shadow-lg">
              <h3 className="text-zinc-400 text-xs font-bold uppercase tracking-wider mb-6">Popularidad de Servicios</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {Object.entries(cuts.reduce((acc, c) => { acc[c.service] = (acc[c.service] || 0) + 1; return acc; }, {})).map(([service, count]) => (
                  <div key={service} className="bg-zinc-800/50 border border-zinc-700/50 p-5 rounded-lg text-center">
                    <p className="font-heading text-3xl font-black text-white mb-1">{count}</p>
                    <span className="text-amber-500 text-xs font-bold uppercase block">{getServiceName(service)}</span>
                  </div>
                ))}
                {cuts.length === 0 && (
                  <div className="col-span-full text-zinc-600 text-center py-8 bg-zinc-950 rounded-lg">No hay datos suficientes</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ============================== */}
        {/* AJUSTES */}
        {/* ============================== */}
        {activeSection === "settings" && (
          <div className="max-w-2xl bg-zinc-900 border border-zinc-800 rounded-lg p-6 sm:p-8 shadow-xl mb-12">
            <div className="flex items-center gap-3 mb-8 border-b border-zinc-800 pb-4">
              <Settings className="w-8 h-8 text-amber-500" />
              <h2 className="font-heading text-2xl font-bold uppercase text-white">Configuración del Perfil</h2>
            </div>
            <div className="space-y-6">
              <div>
                <Label className="text-zinc-400 flex items-center gap-2 mb-2"><MessageCircle className="w-4 h-4 text-green-500" /> Número de WhatsApp</Label>
                <Input value={settingsForm.phone} onChange={(e) => handleSettingsFieldChange('phone', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-12" />
              </div>
              <div>
                <Label className="text-zinc-400 flex items-center gap-2 mb-2"><MapPin className="w-4 h-4 text-amber-500" /> Dirección de la Barbería</Label>
                <Input value={settingsForm.address} onChange={(e) => handleSettingsFieldChange('address', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-12" />
              </div>
              <div>
                <Label className="text-zinc-400 mb-2 block">Enlace de tu Logo (URL)</Label>
                <div className="flex gap-4 items-start">
                  <Input value={settingsForm.logo_url} onChange={(e) => handleSettingsFieldChange('logo_url', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-12" />
                  {settingsForm.logo_url && (
                    <img src={settingsForm.logo_url} alt="Preview" className="w-12 h-12 rounded bg-zinc-800 object-cover flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 border border-zinc-800 rounded-lg p-5 bg-zinc-950/50">
                <div>
                  <Label className="text-zinc-400 flex items-center gap-2 mb-2"><DollarSign className="w-4 h-4 text-green-500" /> Moneda Principal</Label>
                  <Select value={settingsForm.currency} onValueChange={(val) => handleSettingsFieldChange('currency', val)}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-12"><SelectValue placeholder="Moneda" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                      <SelectItem value="DOP">DOP - Peso Dominicano</SelectItem>
                      <SelectItem value="USD">USD - Dólar Estadounidense</SelectItem>
                      <SelectItem value="MXN">MXN - Peso Mexicano</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="COP">COP - Peso Colombiano</SelectItem>
                      <SelectItem value="ARS">ARS - Peso Argentino</SelectItem>
                      <SelectItem value="CLP">CLP - Peso Chileno</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400 flex items-center gap-2 mb-2"><Globe className="w-4 h-4 text-blue-500" /> Idioma del Sistema</Label>
                  <Select value={settingsForm.language} onValueChange={(val) => handleSettingsFieldChange('language', val)}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-12"><SelectValue placeholder="Idioma" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                      <SelectItem value="es">Español</SelectItem>
                      <SelectItem value="en">English (Inglés)</SelectItem>
                      <SelectItem value="pt">Português (Brasil)</SelectItem>
                      <SelectItem value="fr">Français (Francés)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border border-zinc-800 rounded-lg p-5 bg-zinc-950/50">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-white font-heading uppercase text-lg flex items-center gap-2"><Scissors className="w-5 h-5 text-amber-500" /> Tus Servicios</Label>
                  <Button variant="outline" size="sm" onClick={handleAddService} className="border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black font-bold h-8">
                    <Plus className="w-4 h-4 mr-1" /> Añadir
                  </Button>
                </div>
                <div className="space-y-3">
                  {settingsForm.services.map((svc, index) => (
                    <div key={svc.id} className="flex gap-2 items-end bg-zinc-900 p-3 rounded border border-zinc-800">
                      <div className="flex-1">
                        <Label className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider">Nombre</Label>
                        <Input value={svc.name} onChange={(e) => handleServiceChange(index, "name", e.target.value)} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-10" />
                      </div>
                      <div className="w-24">
                        <Label className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider">Precio ({currentCurrency})</Label>
                        <Input type="number" value={svc.price} onChange={(e) => handleServiceChange(index, "price", e.target.value)} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-10 font-bold text-amber-500" />
                      </div>
                      <Button variant="ghost" onClick={() => handleRemoveService(index)} className="h-10 px-3 text-zinc-500 hover:text-red-500 hover:bg-red-500/10">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {settingsForm.services.length === 0 && (
                    <p className="text-sm text-zinc-500 text-center py-4 italic border border-dashed border-zinc-800 rounded">No has agregado ningún servicio.</p>
                  )}
                </div>
              </div>

              <div className="border border-zinc-800 rounded-lg p-5 bg-zinc-950/50">
                <Label className="text-white font-heading uppercase text-lg flex items-center gap-2 mb-4"><Clock className="w-5 h-5 text-amber-500" /> Horario Laborable</Label>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label className="text-zinc-500 text-xs mb-2 block">Hora de Apertura (24h)</Label>
                    <div className="relative">
                      <Input type="number" min="0" max="23" value={settingsForm.business_hours.start} onChange={(e) => handleBusinessHoursChange('start', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-12 pl-4 text-lg font-bold" />
                      <span className="absolute right-4 top-3 text-zinc-500">:00</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs mb-2 block">Hora de Cierre (24h)</Label>
                    <div className="relative">
                      <Input type="number" min="1" max="24" value={settingsForm.business_hours.end} onChange={(e) => handleBusinessHoursChange('end', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-12 pl-4 text-lg font-bold" />
                      <span className="absolute right-4 top-3 text-zinc-500">:00</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-zinc-800">
                <Button onClick={handleUpdateSettings} className="w-full bg-amber-500 text-black hover:bg-amber-400 font-heading uppercase font-bold h-14 text-lg shadow-[0_0_15px_rgba(245,158,11,0.2)]">
                  <Save className="w-6 h-6 mr-2" /> {t("save_changes")}
                </Button>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ============================== */}
      {/* MODALS */}
      {/* ============================== */}

      {/* Notification Modal */}
      <Dialog open={notificationModal} onOpenChange={setNotificationModal}>
        <DialogContent className="bg-zinc-900 border border-zinc-800 text-white w-[95vw] max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase text-amber-500">Enviar Notificación</DialogTitle>
          </DialogHeader>
          {selectedAppointment && (
            <div className="space-y-4 pt-2">
              <div className="bg-zinc-800 p-3 rounded-lg border border-zinc-700">
                <p className="text-white font-semibold">{selectedAppointment.client_name}</p>
                <p className="text-zinc-400 text-sm">{selectedAppointment.client_phone}</p>
              </div>
              <Textarea value={notificationMessage} onChange={(e) => setNotificationMessage(e.target.value)} placeholder="Escribe el mensaje que recibirá el cliente..." className="bg-zinc-800 border-zinc-700 text-white min-h-[100px] resize-none" />
              {whatsappStatus.configured && (
                <div className="flex items-center justify-between bg-zinc-800 p-3 rounded-lg border border-zinc-700">
                  <span className="text-white flex items-center gap-2 font-medium text-sm">
                    <MessageCircle className="w-4 h-4 text-green-500" /> Enviar vía WhatsApp
                  </span>
                  <Switch checked={sendWhatsApp} onCheckedChange={setSendWhatsApp} />
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button variant="outline" onClick={() => setNotificationModal(false)} className="border-zinc-700 text-zinc-400 w-full sm:w-auto hover:bg-zinc-800">Cancelar</Button>
            <Button onClick={handleSendNotification} className="bg-amber-500 text-black font-heading uppercase w-full sm:w-auto hover:bg-amber-400">
              <Send className="w-4 h-4 mr-2" /> Enviar Mensaje
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transaction Modal */}
      <Dialog open={transactionModal} onOpenChange={setTransactionModal}>
        <DialogContent className="bg-zinc-900 border border-zinc-800 text-white w-[95vw] max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase text-amber-500">Nueva Transacción</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex gap-2">
              <Button onClick={() => setNewTransaction({...newTransaction, type: "income"})}
                className={`flex-1 font-heading uppercase text-sm h-12 border ${newTransaction.type === 'income' ? 'bg-green-500/10 border-green-500 text-green-500' : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700'}`}>
                Ingreso
              </Button>
              <Button onClick={() => setNewTransaction({...newTransaction, type: "expense"})}
                className={`flex-1 font-heading uppercase text-sm h-12 border ${newTransaction.type === 'expense' ? 'bg-red-500/10 border-red-500 text-red-500' : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700'}`}>
                Gasto
              </Button>
            </div>
            <div>
              <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Categoría</Label>
              <Input placeholder="Ej: Servicios, Productos, Alquiler" value={newTransaction.category} onChange={(e) => setNewTransaction({...newTransaction, category: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white h-12" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Monto ({currentCurrency})</Label>
              <Input type="number" placeholder="0.00" value={newTransaction.amount} onChange={(e) => setNewTransaction({...newTransaction, amount: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white h-12 font-bold text-lg" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Descripción (Opcional)</Label>
              <Input placeholder="Detalles de la transacción" value={newTransaction.description} onChange={(e) => setNewTransaction({...newTransaction, description: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white h-12" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Fecha</Label>
              <Input type="date" value={newTransaction.date} onChange={(e) => setNewTransaction({...newTransaction, date: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white h-12" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0 mt-6">
            <Button variant="outline" onClick={() => setTransactionModal(false)} className="border-zinc-700 text-zinc-400 w-full sm:w-auto hover:bg-zinc-800">Cancelar</Button>
            <Button onClick={handleCreateTransaction} className="bg-amber-500 text-black font-heading uppercase font-bold w-full sm:w-auto hover:bg-amber-400">Guardar Registro</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inventory Modal */}
      <Dialog open={inventoryModal} onOpenChange={setInventoryModal}>
        <DialogContent className="bg-zinc-900 border border-zinc-800 text-white w-[95vw] max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase text-amber-500">Agregar Producto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Nombre del Producto</Label>
              <Input placeholder="Ej: Cera para Barba, Navajas..." value={newInventoryItem.name} onChange={(e) => setNewInventoryItem({...newInventoryItem, name: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white h-12" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Categoría</Label>
              <Input placeholder="Ej: Herramientas, Cosméticos..." value={newInventoryItem.category} onChange={(e) => setNewInventoryItem({...newInventoryItem, category: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white h-12" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Cantidad Inicial</Label>
                <Input type="number" placeholder="0" value={newInventoryItem.quantity} onChange={(e) => setNewInventoryItem({...newInventoryItem, quantity: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white h-12 font-bold text-center text-lg" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Alerta de Stock Bajo</Label>
                <Input type="number" placeholder="5" value={newInventoryItem.min_quantity} onChange={(e) => setNewInventoryItem({...newInventoryItem, min_quantity: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white h-12 text-center" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Costo Unitario ({currentCurrency})</Label>
                <Input type="number" placeholder="0.00" value={newInventoryItem.unit_price} onChange={(e) => setNewInventoryItem({...newInventoryItem, unit_price: e.target.value})} className="bg-zinc-800 border-zinc-700 text-amber-500 h-12 font-bold" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Proveedor (Opcional)</Label>
                <Input placeholder="Distribuidor..." value={newInventoryItem.supplier} onChange={(e) => setNewInventoryItem({...newInventoryItem, supplier: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white h-12" />
              </div>
            </div>
            {(parseFloat(newInventoryItem.unit_price) > 0 && parseInt(newInventoryItem.quantity) > 0) && (
              <div className="bg-zinc-950 border border-zinc-800 p-3 rounded-lg flex justify-between items-center">
                <span className="text-xs text-zinc-400 uppercase font-bold">Inversión Total:</span>
                <span className="text-red-500 font-bold">
                  {(parseFloat(newInventoryItem.unit_price) * parseInt(newInventoryItem.quantity)).toFixed(2)} {currentCurrency}
                </span>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0 mt-6">
            <Button variant="outline" onClick={() => setInventoryModal(false)} className="border-zinc-700 text-zinc-400 w-full sm:w-auto hover:bg-zinc-800">Cancelar</Button>
            <Button onClick={handleCreateInventoryItem} className="bg-amber-500 text-black font-heading uppercase font-bold w-full sm:w-auto hover:bg-amber-400">Guardar Producto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cut Modal */}
      <Dialog open={cutModal} onOpenChange={setCutModal}>
        <DialogContent className="bg-zinc-900 border border-zinc-800 text-white w-[95vw] max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase text-amber-500">Registrar Corte</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Nombre del Cliente</Label>
              <Input placeholder="Ej: Carlos Gómez" value={newCut.client_name} onChange={(e) => setNewCut({...newCut, client_name: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white h-12" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Servicio Realizado</Label>
              <Select value={newCut.service} onValueChange={(v) => {
                const s = adminProfile?.services?.find(sv => sv.id === v);
                setNewCut({...newCut, service: v, price: s ? s.price.toString() : "0"});
              }}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-12"><SelectValue placeholder="Selecciona un servicio..." /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                  {adminProfile?.services?.map((svc) => (
                    <SelectItem key={svc.id} value={svc.id}>{svc.name} <span className="text-zinc-500 ml-2">- {svc.price} {currentCurrency}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Precio ({currentCurrency})</Label>
                <Input type="number" placeholder="500" value={newCut.price} onChange={(e) => setNewCut({...newCut, price: e.target.value})} className="bg-zinc-800 border-zinc-700 text-green-500 h-12 font-bold text-lg" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Barbero</Label>
                <Input placeholder="Tu nombre" value={newCut.barber_name} onChange={(e) => setNewCut({...newCut, barber_name: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white h-12" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Fecha</Label>
                <Input type="date" value={newCut.date} onChange={(e) => setNewCut({...newCut, date: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white h-12" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Hora</Label>
                <Input type="time" value={newCut.time} onChange={(e) => setNewCut({...newCut, time: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white h-12" />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0 mt-6">
            <Button variant="outline" onClick={() => setCutModal(false)} className="border-zinc-700 text-zinc-400 w-full sm:w-auto hover:bg-zinc-800">Cancelar</Button>
            <Button onClick={handleCreateCut} className="bg-amber-500 text-black font-heading uppercase font-bold w-full sm:w-auto hover:bg-amber-400">Registrar Corte</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default AdminDashboard;